import React, { useEffect, useState } from "react";
import Layout from "../../../../Components/Common/Layout.js";
import DashLayout from "../../../../Components/Admin/AdminDashLayout.js";
import { useDispatch, useSelector } from "react-redux";
import { Button, Drawer, Input, Spin, Table, Tag } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import useNavHeading from "../../../../Hooks/CommonHooks/useNavHeading .js";
import { MdDeleteOutline, MdPostAdd, MdVisibility } from "react-icons/md";
import {
  deleteConfiguration,
  fetchConfiguration,
} from "../../../../Store/Slices/Finance/Configuration/configuration.thunk.js";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ProtectedAction from "../../../../Routes/ProtectedRoutes/ProtectedAction.js";
import { PERMISSIONS } from "../../../../config/permission.js";
import ProtectedSection from "../../../../Routes/ProtectedRoutes/ProtectedSection.js";
import { fetchPayrollStaff } from "../../../../Store/Slices/Finance/payroll/payroll.thunk.js";
import { Modal } from "antd";
import { ExclamationCircleFilled } from "@ant-design/icons";
const { confirm } = Modal;

const PayrollManagement = () => {
  const { t } = useTranslation("prtFinance");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const role = useSelector((store) => store.common.auth.role);
  const schoolCurrency = useSelector(
    (store) => store.common.user.userDetails?.currency
  );
  const { configurations, loading, total, totalPages } = useSelector(
    (store) => store.admin.configuration
  );

  useNavHeading("HR & Payroll", `Configuration`);

  const [searchText, setSearchText] = useState("");
  const [configurationType, setConfigurationType] = useState("payroll");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [viewDrawerOpen, setViewDrawerOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

 

  useEffect(() => {
    dispatch(
      fetchConfiguration({
        configType: configurationType,
        search: searchText,
        page: currentPage,
        limit: pageSize,
      })
    );
  }, [dispatch, configurationType, searchText, currentPage, pageSize]);

  const handleView = (record) => {
    setSelectedRecord(record);
    setViewDrawerOpen(true);
  };
function handleDelete(id) {
    confirm({
      title: "Are you sure you want to delete this configuration?",
      icon: <ExclamationCircleFilled style={{ color: "#faad14" }} />,
      content: "This action cannot be undone.",
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "Cancel",
      centered: true,
      onOk() {
        dispatch(deleteConfiguration(id));
      },
    });
  }
  const columns = [
    {
      title: t("Users"),
      key: "Total Staff",
      render: (record) => (
        <div className="flex flex-wrap gap-2">
              <Tag  color="purple" className="rounded-xl w-[10rem]">
                <div className="flex flex-col gap-1  p-2">
                  <span className="font-medium text-sm text-blue-600">
                    {record?.configData?.staffIds?.length}
                  </span >
                </div>
              </Tag>
        </div>
      ),
    },
    {
      title: t("Description"),
      dataIndex: "description",
      key: "description",
    },
    {
      title: t("Action"),
      key: "action",
      render: (_, record) => (
        <div className="flex items-center flex-row gap-3 justify-center">
          <ProtectedAction
            requiredPermission={PERMISSIONS.APPLY_FINANCE_CONFIGURATION}
          >
            <button
              className="flex items-center gap-2"
              title={t("Apply")}
              onClick={() => {
                if (record.configType === "entityRevenue")
                  return navigate("/finance/entity/revenue/add", {
                    state: record,
                  });
                if (record.configType === "studentRevenue")
                  return navigate("/finance/studentfees/add/form", {
                    state: record,
                  });
                if (record.configType === "payroll")
                  return navigate("/finance/payroll/add", { state: record });
                if (record.configType === "generalExpense")
                  return navigate("/finance/operational-expenses/add", {
                    state: record,
                  });
              }}
            >
              {t("Apply")} <MdPostAdd size={20} />
            </button>
          </ProtectedAction>

          <ProtectedAction
            requiredPermission={PERMISSIONS.DELETE_FINANCE_CONFIGURATION}
          >
            <button
              title={t("Delete")}
              onClick={() => handleDelete(record._id)}
            >
              <MdDeleteOutline size={20} />
            </button>
          </ProtectedAction>
          <Button
            type="link"
            icon={<MdVisibility size={20} />}
            onClick={() => handleView(record)}
          ></Button>
        </div>
      ),
      width: 120,
    },
  ];

  return (
    <Layout title={`Finance | Office Flow`}>
      <DashLayout>
        <ProtectedSection
          requiredPermission={PERMISSIONS.VIEW_FINANCE_CONFIGURATION}
          title={"Finance Configuration"}
        >
          <div className="p-4">
            {/* Filters */}
            <div className="flex flex-row items-center justify-between mb-4 gap-4">
              <div className="flex w-full items-center gap-4 justify-between">
                <Input
                  placeholder={t("Search...")}
                  prefix={<SearchOutlined />}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  allowClear
                  className="w-80 py-2"
                />
                <Button
                  onClick={() => navigate("/AddPayrollManagement")}
                  className="ml-10 bg-gradient-to-r from-pink-500 to-purple-500 text-white"
                >
                  {t("Add New")}
                </Button>
              </div>
            </div>

            {/* Table */}
            <Table
              dataSource={configurations}
              columns={columns}
              pagination={{
                current: currentPage,
                total,
                pageSize,
                showSizeChanger: true,
                pageSizeOptions: ["5", "10", "20", "50"],
                size: "small",
                showTotal: (total) =>
                  `Page ${currentPage} of ${totalPages} | Total ${total} records`,
                onChange: (page, size) => {
                  setCurrentPage(page);
                  setPageSize(size);
                  dispatch(
                    fetchConfiguration({
                      configType: configurationType,
                      search: searchText,
                      page,
                      limit: size,
                    })
                  );
                },
              }}
              loading={{ spinning: loading, indicator: <Spin size="large" /> }}
              bordered
              rowKey={(record) => record._id}
            />
          </div>

          <Drawer
            title={
              <span className="font-semibold text-lg text-pink-500">
                Payroll Configuration Details
              </span>
            }
            open={viewDrawerOpen}
            onClose={() => setViewDrawerOpen(false)}
            width={"60%"}
          >
            {selectedRecord ? (
              <div className="space-y-6 text-sm">
                {/* ===== Summary Section ===== */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 bg-purple-50 p-4 rounded-xl border border-purple-200">
                  <div>
                    <p className="font-semibold text-gray-700">Description</p>
                    <p className="text-gray-800">
                      {selectedRecord.description || "—"}
                    </p>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-700">
                      Configuration Type
                    </p>
                    <p className="text-gray-800 capitalize">
                      {selectedRecord.configType || "—"}
                    </p>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-700">Frequency</p>
                    <p className="text-gray-800">
                      {selectedRecord.configData?.lineItems?.[0]?.frequency ||
                        "N/A"}
                    </p>
                  </div>
                </div>

                {/* ===== Staff Members ===== */}
                {/* <div>
                  <h3 className="font-semibold text-base text-purple-700 mb-2">
                    Staff Members
                  </h3>
                  <div className="grid md:grid-cols-2 gap-3">
                    {staffs
                      ?.filter((s) =>
                        selectedRecord?.configData?.staffIds?.includes(s._id)
                      )
                      ?.map((st) => (
                        <div
                          key={st._id}
                          className="p-4 border border-gray-200 rounded-xl bg-white shadow-sm hover:shadow-md transition-all"
                        >
                          <div className="flex justify-between items-center mb-1">
                            <p className="font-semibold text-blue-600 text-sm">
                              {st.firstName} {st.lastName}
                            </p>
                            <Tag color="purple">{st.employeeID}</Tag>
                          </div>
                          <p className="text-xs text-gray-500 mb-1">
                            Email: {st.email}
                          </p>
                          <p className="text-xs text-gray-500">
                            Salary: {schoolCurrency} {st.monthlySalary || "—"}
                          </p>
                        </div>
                      ))}
                  </div>
                </div> */}

                {/* ===== Salary Details ===== */}
                <div>
                  <h3 className="font-semibold text-base text-purple-700 mb-2">
                    Salary Details
                  </h3>
                  {selectedRecord.configData?.lineItems?.map((item, index) => (
                    <div
                      key={index}
                      className="border rounded-xl bg-gray-50 p-4 mb-3 shadow-sm"
                    >
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        <p>
                          <strong>Basic Salary:</strong> {item.basicSalary}
                        </p>
                        <p>
                          <strong>Allowances:</strong> {item.allowances}
                        </p>
                        <p>
                          <strong>Bonus:</strong> {item.bonus}
                        </p>
                        <p>
                          <strong>Deductions:</strong> {item.deductions}
                        </p>
                        <p>
                          <strong>Tax:</strong> {item.tax}
                        </p>
                        <p>
                          <strong>Net Salary:</strong> {item.netSalary}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-gray-500">No data selected</p>
            )}
          </Drawer>
        </ProtectedSection>
      </DashLayout>
    </Layout>
  );
};

export default PayrollManagement;
